import React from 'react';
import KeystoneLogo, { KeystoneMark } from '../components/KeystoneLogo';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Keystone by CloudVoro, Sales Brochure (v4, "Start With Why" arc)
 *
 * Purpose: a full A4 hand-over document for a first meeting with a
 *   founder/owner of a 5-50-person food manufacturer, and classy enough
 *   to hand an investor. Also usable as a cold-email attachment.
 *
 * Narrative: WHY (belief, pages 1-2) -> the buyer's reality (3) ->
 *   HOW (learned on the floor, 4) -> WHAT (spine + workflows, 5-9) ->
 *   proof (10-12) -> offer (13-14) -> fit + close (15-16).
 *
 * Voice: founder-written, no vendor cliches. No specific price quoted;
 *   no Founder's Club seat count revealed. No em-dashes.
 *
 * Public route. Print-perfect A4. Server-rendered to PDF via
 *   /api/marketing/render-pdf (path whitelisted in backend/routes/marketing.py).
 */

const TOTAL_PAGES = 17;

const C = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  navyMid: '#163E66',
  magenta: '#E91E63',
  magentaDark: '#C2185B',
  gold: '#C9A961',
  goldSoft: '#E5D2A6',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#0F1B2A',
  slate: '#3D4A5C',
  muted: '#6B7280',
  border: '#E2E8F0',
  bgSoft: '#F7FAFC',
  green: '#166534',
  red: '#991B1B',
  amber: '#92400E',
};

// ---------- Page primitives ----------

const Page = ({ children, bg = '#fff', noPad = false, last = false, pageNum }) => (
  <section
    className="brochure-page"
    data-page={pageNum}
    style={{
      width: '210mm',
      height: '297mm',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: noPad ? 0 : '14mm 16mm',
      boxSizing: 'border-box',
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: C.ink,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum, dark = false }) => (
  <div
    style={{
      position: 'absolute',
      top: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8.5pt',
      color: dark ? 'rgba(255,255,255,0.65)' : C.muted,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <KeystoneMark size={20} variant={dark ? 'dark' : 'light'} />
      <span style={{ color: dark ? '#fff' : C.navy, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '9.5pt' }}>Keystone</span>
      <span style={{ fontSize: '7.5pt' }}>·</span>
      <span style={{ color: dark ? C.gold : C.magenta, fontSize: '7.5pt', letterSpacing: '0.15em' }}>BY CLOUDVORO</span>
    </div>
    <span>Buyer pack · {pageNum} / {TOTAL_PAGES}</span>
  </div>
);

const PageFooter = ({ dark = false }) => (
  <div
    style={{
      position: 'absolute',
      bottom: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8pt',
      color: dark ? 'rgba(255,255,255,0.55)' : C.muted,
      borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.15)' : C.border}`,
      paddingTop: '4mm',
    }}
  >
    <span>The operations layer for European food manufacturers · keystone.cloudvoro.com</span>
    <span style={{ color: dark ? C.gold : C.magenta, fontWeight: 600 }}>hello@cloudvoro.com</span>
  </div>
);

const SectionLabel = ({ children, color = C.magenta }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontSize: '8.5pt', letterSpacing: '0.18em',
    textTransform: 'uppercase', fontWeight: 700,
    color, marginBottom: '6mm',
  }}>
    <span style={{ width: 24, height: 2, background: color, display: 'inline-block' }} />
    {children}
  </div>
);

const H2 = ({ children, style = {} }) => (
  <h2 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '30pt',
    fontWeight: 700,
    color: C.navy,
    lineHeight: 1.05,
    letterSpacing: '-0.02em',
    margin: '0 0 6mm',
    ...style,
  }}>{children}</h2>
);

const Em = ({ children, color = C.magenta }) => (
  <em style={{ fontStyle: 'italic', fontWeight: 500, color }}>{children}</em>
);

const Body = ({ children, style = {} }) => (
  <p style={{
    fontSize: '10.5pt',
    lineHeight: 1.55,
    color: C.slate,
    margin: '0 0 4mm',
    ...style,
  }}>{children}</p>
);

const Pull = ({ children, color = C.magenta }) => (
  <div style={{
    borderLeft: `3px solid ${color}`,
    paddingLeft: '5mm',
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '14pt',
    fontStyle: 'italic',
    color: C.navy,
    lineHeight: 1.35,
    margin: '4mm 0',
  }}>{children}</div>
);

// ---------- Page 1: Cover ----------

const CoverPage = () => (
  <Page bg={C.navy} noPad pageNum={1}>
    <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 20% 15%, ${C.navyMid} 0%, ${C.navy} 45%, ${C.navyDeep} 100%)` }} />
    <div style={{ position: 'absolute', top: '18mm', right: '-40mm', width: '200mm', height: '200mm', background: `radial-gradient(circle, ${C.magenta}22 0%, transparent 60%)`, filter: 'blur(20px)' }} />

    <div style={{ position: 'relative', height: '100%', padding: '16mm 18mm', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff', boxSizing: 'border-box' }}>
      {/* Top strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <KeystoneLogo height={48} variant="dark" showTagline={false} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 6 }}>
            Sales brochure · v4
          </div>
          <div style={{ fontSize: '8.5pt', color: 'rgba(255,255,255,0.55)' }}>2026 · EU + UK</div>
        </div>
      </div>

      {/* Headline */}
      <div>
        <div style={{ fontSize: '9pt', letterSpacing: '0.28em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '8mm' }}>
          Why Keystone exists
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '40pt',
          fontWeight: 700,
          lineHeight: 1.02,
          letterSpacing: '-0.03em',
          margin: 0,
          color: '#fff',
        }}>
          You shouldn’t need<br/>
          <Em color={C.gold}>an enterprise ERP to prove</Em><br/>
          <Em color={C.gold}>your food is safe.</Em>
        </h1>
        <p style={{
          fontSize: '12.5pt',
          lineHeight: 1.45,
          color: 'rgba(255,255,255,0.82)',
          marginTop: '9mm',
          maxWidth: '150mm',
          fontWeight: 300,
        }}>
          We believe that, so we built the alternative beside a cheese vat in Tipperary. Keystone is the operations layer between your factory floor and your accounts system: sales orders, batch traceability, dispatch, retailer EDI, COAs and mock recalls in one system.
        </p>
      </div>

      {/* Footer strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '4mm' }}>
        <div>
          <div style={{ fontSize: '8pt', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', fontWeight: 600, marginBottom: 4 }}>
            Founding partner
          </div>
          <div style={{ fontSize: '10.5pt', color: '#fff', fontWeight: 500 }}>Cashel Blue, 40+ SKUs, 9 export markets</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '9pt', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
          hello@cloudvoro.com<br/>
          <span style={{ color: C.gold }}>keystone.cloudvoro.com</span>
        </div>
      </div>
    </div>
  </Page>
);

// ---------- Page 2: The belief (WHY) ----------

const BeliefPage = () => (
  <Page pageNum={2} bg={C.cream}>
    <PageHeader pageNum={2} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>01 · Why we exist</SectionLabel>
      <H2>We started with a belief,<br/><Em>not a product.</Em></H2>

      <Body>
        We came from enterprise IT. Then we started working inside smaller food manufacturers and found something strange: businesses producing millions of euro of food were still depending on spreadsheets, paper and one person&rsquo;s memory. The alternatives quoted to them were often far larger than the problem, €40k+ ERPs configured to fit but never quite fitting.
      </Body>
      <Body>
        So instead of designing another ERP from an office, we built Keystone inside a working food factory, with the QA manager watching the screen and a Tuesday-morning order deadline closing in. Keystone wasn&rsquo;t designed for a market segment and then sold downwards. It was designed for real people on a real floor, then generalised.
      </Body>

      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4mm' }}>
        {[
          ['Traceability should take 30 seconds', 'Not a weekend of spreadsheets. The four-hour audit clock should feel generous, not terrifying.'],
          ['No single point of failure', 'A family food business shouldn\u2019t be one resignation away from losing its records.'],
          ['Software for factories is written in factories', 'Every feature exists because a producer needed it that week, not because a roadmap workshop predicted it.'],
          ['Honesty is a feature', 'If a competitor genuinely fits you better, we say so. In writing, on our own website.'],
        ].map(([t, b], i) => (
          <div key={i} style={{ padding: '4.5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.magenta}` }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 3 }}>
              <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', fontWeight: 700, color: C.magenta, lineHeight: 1 }}>0{i + 1}</span>
              <div style={{ fontSize: '10.5pt', fontWeight: 700, color: C.navy, lineHeight: 1.25 }}>{t}</div>
            </div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5, paddingLeft: 22 }}>{b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '5mm', padding: '4.5mm 5mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2.5mm' }}>What you can expect from us</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5mm 6mm', fontSize: '9pt', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
          <div><strong style={{ color: '#fff' }}>Direct access.</strong> You work with the people implementing the system.</div>
          <div><strong style={{ color: '#fff' }}>Your data remains yours.</strong> Export it whenever you need it.</div>
          <div><strong style={{ color: '#fff' }}>Pricing in writing.</strong> Licence and implementation separated clearly.</div>
          <div><strong style={{ color: '#fff' }}>Only what we can evidence.</strong> Roadmap items labelled as roadmap items.</div>
          <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: '#fff' }}>Defined implementation.</strong> Scope and price agreed before work begins.</div>
        </div>
      </div>

      <div style={{ marginTop: '5mm', padding: '3.5mm 5mm', background: C.gold + '22', border: `1px solid ${C.gold}66` }}>
        <div style={{ fontSize: '9.5pt', color: C.navy, lineHeight: 1.55 }}>
          <strong>The gap we occupy.</strong> Between Excel and enterprise ERP sit tens of thousands of European food producers with real traceability obligations and no software built for their scale. The rest of this document is our belief, made specific.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 3: The operational reality ----------

const RealityPage = () => (
  <Page pageNum={3}>
    <PageHeader pageNum={3} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>02 · The operational reality</SectionLabel>
      <H2>Running a small food business<br/><Em>is a paper factory bolted onto a food factory.</Em></H2>

      <Body>
        Somewhere between the ten-litre milk churn and the retailer’s dock, a founder’s week disappears. It gets spent chasing kill sheets, hand-typing dispatch notes, matching customer purchase orders against internal SKU codes, printing COAs at four in the afternoon, and remembering which spreadsheet holds this month’s allergen list.
      </Body>

      <Body>
        The accounts package handles the invoice. The factory handles the making. Everything in between, the operational middle, runs on Excel, WhatsApp, sticky notes and one person’s memory.
      </Body>

      <Pull>
        Most food businesses aren’t drowning in bad software. They’re drowning in the <em>absence</em> of the right software.
      </Pull>

      <div style={{ marginTop: '10mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm' }}>
        {[
          { label: 'Friday afternoon', body: 'A retailer emails: “which batches went into last week’s Tesco delivery?” You open three spreadsheets.' },
          { label: 'Sunday night', body: 'Someone types tomorrow’s dispatch notes by hand because the accounts package prints them wrong.' },
          { label: 'The quiet fear', body: 'If there was a recall on Tuesday, who would find the affected batches by Wednesday morning? Honestly?' },
          { label: 'The audit before the audit', body: 'Two weeks of evenings spent making sure the paper trail agrees with the paper trail.' },
        ].map((b) => (
          <div key={b.label} style={{ padding: '5mm', background: C.bgSoft, borderLeft: `3px solid ${C.magenta}` }}>
            <div style={{ fontSize: '7.5pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: 4 }}>{b.label}</div>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: b.body }} />
          </div>
        ))}
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 4: Learned on the floor ----------

const WhyGenericPage = () => (
  <Page pageNum={4} bg={C.cream}>
    <PageHeader pageNum={4} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>03 · Learned on the floor</SectionLabel>
      <H2>Eight quiet realities of food manufacturing<br/><Em>you only learn standing beside the vat.</Em></H2>

      <Body style={{ marginBottom: '8mm' }}>
        General-purpose ERP can be made to run food manufacturing. Odoo, Business Central, Sage and NetSuite all have food-industry configurations available from partners. The question isn&rsquo;t whether it can be done — it&rsquo;s how much configuration, consulting, and operational change it takes to get there. These eight realities are what Keystone starts with instead of what it&rsquo;s configured to eventually reach:
      </Body>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4mm 6mm' }}>
        {[
          ['Every retailer wants their own SKU code', 'Tesco calls it FBWHL14. You call it CB-200. The dispatch note has to match the retailer’s. Get it wrong and it’s rejected at the door.'],
          ['Nothing weighs what the recipe says', 'A 1.4 kg wheel is really 1.36 kg to 1.44 kg. Case counts round. Invoices need catch-weight totals, not nominal ones.'],
          ['Recalls aren’t a report, they’re a countdown', 'From “which batches?” to “which customers received them?” you have four hours, not four days. And they always ask on a Friday.'],
          ['Batch corrections happen', 'You mis-labelled a batch on Tuesday. Fixing it in October cannot break the audit trail from July. Most systems just overwrite.'],
          ['Co-packing is a first-class case', 'Same cheese, three retailer wrappers. Same batch, three private-label variants. Traceability has to survive that.'],
          ['Ingredient lots matter, not just batches', 'When the flour supplier issues a lot recall at 5pm, you need to know which finished batches used which flour drums, in minutes.'],
          ['Audit logs must be tamper-evident', 'When you correct a mis-label from Tuesday six months later, the auditor needs to see both the original entry and the correction. Most systems just overwrite. Keystone preserves what changed, when, and by whom.'],
          ['Documents are the product too', 'A dispatch note with the wrong pallet count is a rejected delivery. A COA missing a signature is a rejected pallet. Templates matter.'],
        ].map(([title, body], i) => (
          <div key={i} style={{ padding: '4mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `2px solid ${C.magenta}` }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
              <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', fontWeight: 700, color: C.magenta, lineHeight: 1 }}>{String(i + 1).padStart(2, '0')}</span>
              <div style={{ fontSize: '10pt', fontWeight: 700, color: C.navy, lineHeight: 1.25 }}>{title}</div>
            </div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5, paddingLeft: 22 }} dangerouslySetInnerHTML={{ __html: body }} />
          </div>
        ))}
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 4: The Keystone spine ----------

const SpinePage = () => (
  <Page pageNum={4}>
    <PageHeader pageNum={4} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>03 · What Keystone actually is</SectionLabel>
      <H2>One operational spine.<br/><Em>Four straight lines from milk to money.</Em></H2>

      <Body>
        We didn’t design Keystone as a set of modules. We designed it as a spine, four stages every food manufacturer goes through, and made sure every screen, every audit log, every document falls onto that spine in the same place.
      </Body>

      <div style={{ marginTop: '8mm', position: 'relative' }}>
        {/* Backbone line */}
        <div style={{ position: 'absolute', top: 32, left: '5%', right: '5%', height: 2, background: `linear-gradient(90deg, ${C.magenta} 0%, ${C.gold} 100%)`, zIndex: 0 }} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3mm', position: 'relative', zIndex: 1 }}>
          {[
            { n: '01', name: 'Inwards', color: C.magenta, lines: ['Suppliers', 'Milk intake', 'Silos', 'Ingredient lots', 'Packaging lots', 'Goods-in records'] },
            { n: '02', name: 'Production', color: C.magentaDark, lines: ['Make days', 'Cheese / product batches', 'Recipe as-built', 'QC & release', 'Batch corrections'] },
            { n: '03', name: 'Outwards', color: C.navyMid, lines: ['Sales orders', 'Weekly planner', 'Dispatches', 'Retailer SKU maps', 'Catch-weight rounding', 'Pallet labels · COAs · EDI'] },
            { n: '04', name: 'Traceability', color: C.gold, lines: ['Backward trace', 'Forward trace', 'Mock recall report', 'Customer draft comms', 'Tamper-evident audit log'] },
          ].map((s) => (
            <div key={s.n} style={{ background: C.paper, border: `1px solid ${C.border}`, padding: '5mm 4mm', paddingTop: '10mm', position: 'relative' }}>
              <div style={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', width: 42, height: 42, borderRadius: '50%', background: s.color, color: '#fff', fontFamily: "'Playfair Display', Georgia, serif", fontSize: '14pt', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(10,37,64,0.15)' }}>
                {s.n}
              </div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '15pt', fontWeight: 700, color: C.navy, textAlign: 'center', marginBottom: '3mm', letterSpacing: '-0.01em' }}>{s.name}</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {s.lines.map(l => (
                  <li key={l} style={{ fontSize: '8.5pt', color: C.slate, padding: '2px 0', display: 'flex', gap: 6, lineHeight: 1.35 }}>
                    <span style={{ color: s.color, flexShrink: 0 }}>•</span>
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: '10mm', padding: '5mm', background: C.navy, color: '#fff', borderLeft: `3px solid ${C.gold}` }}>
        <div style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 4 }}>Design principle</div>
        <div style={{ fontSize: '11pt', lineHeight: 1.5, color: '#fff' }}>
          Every event on the floor writes to <em>one</em> chain. A milk delivery on Monday, a make day on Tuesday, a dispatch on Friday, a customer complaint in October, all reachable from the same trace query, in seconds, on any device.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 5: Inwards workflow with mock screen ----------

const WorkflowPage = ({ pageNum, stageLabel, stageColor = C.magenta, title, italic, body, screens = [], bullets }) => (
  <Page pageNum={pageNum} bg={C.paper}>
    <PageHeader pageNum={pageNum} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel color={stageColor}>{stageLabel}</SectionLabel>
      <H2>{title}<br/><Em color={stageColor}>{italic}</Em></H2>
      <Body>{body}</Body>

      <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: '6mm', marginTop: '4mm' }}>
        <div style={{ background: C.bgSoft, border: `1px solid ${C.border}`, padding: '5mm', minHeight: '110mm' }}>
          {screens}
        </div>
        <div>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: stageColor, marginBottom: '4mm' }}>
            What’s actually happening
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {bullets.map((b, i) => (
              <li key={i} style={{ padding: '3mm 0', borderBottom: i < bullets.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: '10pt', color: C.slate, lineHeight: 1.5 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ flexShrink: 0, width: 18, height: 18, borderRadius: '50%', background: stageColor, color: '#fff', fontSize: '8pt', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>{i + 1}</span>
                  <span dangerouslySetInnerHTML={{ __html: b }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// Mock: Inward delivery
const InwardScreen = (
  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '9pt' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3mm', paddingBottom: '2mm', borderBottom: `1px solid ${C.border}` }}>
      <div>
        <div style={{ fontWeight: 700, color: C.navy, fontSize: '11pt' }}>IN-2026-00074</div>
        <div style={{ color: C.muted, fontSize: '8pt', marginTop: 2 }}>Tipperary Milk Co. · Delivery #DN-8821</div>
      </div>
      <div style={{ padding: '2px 8px', background: C.green, color: '#fff', fontSize: '7pt', fontWeight: 700, letterSpacing: '0.08em', borderRadius: 3, height: 16, alignSelf: 'flex-start' }}>ACCEPTED</div>
    </div>
    <div style={{ marginBottom: '3mm', fontSize: '7.5pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.muted, fontWeight: 700 }}>Delivery lines</div>
    <table style={{ width: '100%', fontSize: '8pt', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#fff', borderBottom: `1px solid ${C.border}` }}>
          <th style={{ textAlign: 'left', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Item</th>
          <th style={{ textAlign: 'left', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Supplier lot</th>
          <th style={{ textAlign: 'right', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Qty</th>
          <th style={{ textAlign: 'left', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>State</th>
        </tr>
      </thead>
      <tbody>
        {[
          ['Raw whole milk', 'MI-2026-00074', '2,480 L', 'accepted'],
          ['Salt (food grade)', 'MA4001-26A007', '25 kg', 'accepted'],
          ['Rennet', 'RN-26B112', '2 L', 'accepted'],
          ['Cheese wax', 'WAX-2602-L1', '18 kg', 'accepted'],
          ['Foil wrap', 'FOIL-2602-R8', '2,000 sh', 'accepted'],
        ].map((r, i) => (
          <tr key={i} style={{ background: i % 2 ? C.bgSoft : '#fff' }}>
            <td style={{ padding: '3px 4px', color: C.navy, fontWeight: 500 }}>{r[0]}</td>
            <td style={{ padding: '3px 4px', color: C.slate, fontFamily: 'monospace', fontSize: '7.5pt' }}>{r[1]}</td>
            <td style={{ padding: '3px 4px', color: C.slate, textAlign: 'right' }}>{r[2]}</td>
            <td style={{ padding: '3px 4px', color: C.green, fontSize: '7.5pt' }}>&check; {r[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ marginTop: '3mm', paddingTop: '2mm', borderTop: `1px solid ${C.border}`, fontSize: '7.5pt', color: C.muted }}>
      Antibiotic screen: <span style={{ color: C.green, fontWeight: 700 }}>PASS</span> · SCC 148,000 · TBC 25,000 · Fat 4.2% · Protein 3.5%
    </div>
  </div>
);

// Mock: Make day
const MakeDayScreen = (
  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '9pt' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3mm', paddingBottom: '2mm', borderBottom: `1px solid ${C.border}` }}>
      <div>
        <div style={{ fontWeight: 700, color: C.navy, fontSize: '11pt' }}>MD-2026-00074</div>
        <div style={{ color: C.muted, fontSize: '8pt', marginTop: 2 }}>Tipperary Blue · 10 Feb 2026 · Sup. M. Kowalska</div>
      </div>
      <div style={{ padding: '2px 8px', background: C.magenta, color: '#fff', fontSize: '7pt', fontWeight: 700, letterSpacing: '0.08em', borderRadius: 3, height: 16, alignSelf: 'flex-start' }}>RELEASED</div>
    </div>
    {[
      { title: 'Milk used', rows: [['Silo 2', 'MI-2026-00074', '2,480 L']] },
      { title: 'Ingredients used', rows: [['Salt', 'MA4001-26A007', '24.2 kg'], ['Rennet', 'RN-26B112', '1.6 L'], ['Cultures', 'FS-26A044', '620 g']] },
      { title: 'Packaging used', rows: [['Wax', 'WAX-2602-L1', '17.4 kg'], ['Foil', 'FOIL-2602-R8', '480 sh']] },
      { title: 'Output batches', rows: [['CB-2026-014', 'Tipperary Blue 200g', '480 units'], ['CB-2026-015', 'Tipperary Blue 1.4kg', '48 wheels']] },
    ].map((sec, i) => (
      <div key={i} style={{ marginBottom: '2.5mm' }}>
        <div style={{ fontSize: '7.5pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.muted, fontWeight: 700, marginBottom: 2 }}>{sec.title}</div>
        {sec.rows.map((r, j) => (
          <div key={j} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr', fontSize: '8pt', color: C.slate, padding: '2px 4px', background: j % 2 ? C.bgSoft : 'transparent' }}>
            <span style={{ color: C.navy, fontWeight: 500 }}>{r[0]}</span>
            <span style={{ fontFamily: 'monospace', fontSize: '7.5pt' }}>{r[1]}</span>
            <span style={{ textAlign: 'right' }}>{r[2]}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

// Mock: Dispatch with retailer SKU mapping
const DispatchScreen = (
  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '9pt' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3mm', paddingBottom: '2mm', borderBottom: `1px solid ${C.border}` }}>
      <div>
        <div style={{ fontWeight: 700, color: C.navy, fontSize: '11pt' }}>DSP-2026-00312</div>
        <div style={{ color: C.muted, fontSize: '8pt', marginTop: 2 }}>Tesco Ireland Distribution · PO 4416782</div>
      </div>
      <div style={{ padding: '2px 8px', background: C.navyMid, color: '#fff', fontSize: '7pt', fontWeight: 700, letterSpacing: '0.08em', borderRadius: 3, height: 16, alignSelf: 'flex-start' }}>DISPATCHED</div>
    </div>
    <table style={{ width: '100%', fontSize: '8pt', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#fff', borderBottom: `1px solid ${C.border}` }}>
          <th style={{ textAlign: 'left', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Your code</th>
          <th style={{ textAlign: 'left', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Our SKU</th>
          <th style={{ textAlign: 'right', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Cases</th>
          <th style={{ textAlign: 'right', padding: '3px 4px', color: C.muted, fontWeight: 600 }}>Catch-wt</th>
        </tr>
      </thead>
      <tbody>
        {[
          ['TES-FBWHL14-742', 'CB-1400-STD', '12', '16.83 kg'],
          ['TES-FBWDG20-315', 'CB-200-STD', '48', '9.72 kg'],
          ['TES-FBSLC10-908', 'CB-100-SLC', '24', '2.46 kg'],
        ].map((r, i) => (
          <tr key={i} style={{ background: i % 2 ? C.bgSoft : '#fff' }}>
            <td style={{ padding: '3px 4px', color: C.magenta, fontWeight: 600, fontFamily: 'monospace', fontSize: '7.5pt' }}>{r[0]}</td>
            <td style={{ padding: '3px 4px', color: C.navy, fontFamily: 'monospace', fontSize: '7.5pt' }}>{r[1]}</td>
            <td style={{ padding: '3px 4px', color: C.slate, textAlign: 'right' }}>{r[2]}</td>
            <td style={{ padding: '3px 4px', color: C.navy, textAlign: 'right', fontWeight: 600 }}>{r[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ marginTop: '3mm', padding: '3mm', background: '#fff', border: `1px dashed ${C.border}`, fontSize: '7.5pt', color: C.muted, lineHeight: 1.5 }}>
      <strong style={{ color: C.navy }}>Documents generated:</strong>
      <div>· Dispatch note (Tesco template)</div>
      <div>· Pallet label × 3 (SSCC + retailer barcodes)</div>
      <div>· COA per batch (12 sheets)</div>
      <div>· EDI DESADV / ASN, queued</div>
    </div>
  </div>
);

// Mock: Mock recall report
const RecallScreen = (
  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '9pt' }}>
    <div style={{ padding: '3mm', background: C.red, color: '#fff', marginBottom: '3mm' }}>
      <div style={{ fontSize: '8pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Mock recall report</div>
      <div style={{ fontSize: '10.5pt', fontWeight: 600 }}>Starting point: Salt lot MA4001-26A007</div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3, marginBottom: '3mm' }}>
      {[['Make days', '3'], ['Batches', '11'], ['Dispatches', '18'], ['Customers', '9'], ['Units', '5,940']].map((s, i) => (
        <div key={i} style={{ background: '#fff', border: `1px solid ${C.border}`, padding: '3mm 2mm', textAlign: 'center' }}>
          <div style={{ fontSize: '7pt', color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s[0]}</div>
          <div style={{ fontSize: '13pt', fontWeight: 700, color: C.navy, marginTop: 2, fontFamily: "'Playfair Display', Georgia, serif" }}>{s[1]}</div>
        </div>
      ))}
    </div>
    <div style={{ fontSize: '7.5pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.muted, fontWeight: 700, marginBottom: 2 }}>Downstream customers</div>
    {[
      ['Tesco Ireland Distribution', '3 disp.', '1,842 units'],
      ['Sheridan\u2019s Cheesemongers', '2 disp.', '384 units'],
      ['La Rousse Foods', '2 disp.', '620 units'],
      ['Avoca Handweavers', '1 disp.', '204 units'],
      ['+ 5 others', '10 disp.', '2,890 units'],
    ].map((r, i) => (
      <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', fontSize: '8pt', color: C.slate, padding: '2px 4px', background: i % 2 ? C.bgSoft : 'transparent' }}>
        <span style={{ color: C.navy, fontWeight: 500 }}>{r[0]}</span>
        <span style={{ textAlign: 'right' }}>{r[1]}</span>
        <span style={{ textAlign: 'right', fontWeight: 600 }}>{r[2]}</span>
      </div>
    ))}
    <div style={{ marginTop: '3mm', padding: '2mm 3mm', background: C.amber + '22', border: `1px solid ${C.amber}55`, color: C.amber, fontSize: '8pt', lineHeight: 1.4 }}>
      <strong>Generated in 4.2 seconds.</strong> Customer draft communications ready. Export as PDF response pack.
    </div>
  </div>
);

// ---------- Page 10: Day in the life ----------

const DayInLifePage = () => (
  <Page pageNum={10} bg={C.cream}>
    <PageHeader pageNum={10} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>05 · A day in the life</SectionLabel>
      <H2>What a Friday actually looks like<br/><Em>once Keystone is running.</Em></H2>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4mm' }}>
        {[
          {
            role: 'The founder',
            color: C.magenta,
            hours: [
              ['06:45', 'Coffee. Opens Order Pipeline on the phone. Sees next week’s make plan already sequenced against retailer POs.'],
              ['09:20', 'Retailer emails asking about a delivery from three weeks ago. Types the dispatch number. Full trace on screen in eight seconds.'],
              ['14:00', 'Reviews the week’s catch-weight variance vs nominal, sees a batch trending high, flags it for the make team.'],
              ['16:40', 'Signs off Monday’s dispatch pack. Goes home.'],
            ],
          },
          {
            role: 'The production supervisor',
            color: C.magentaDark,
            hours: [
              ['07:00', 'Make day opens on the tablet in the make room. Milk intake auto-linked to today’s silo.'],
              ['10:30', 'Adds an ingredient lot to a batch as it happens. No paper, no re-typing at 5pm.'],
              ['13:15', 'Closes the make day. Two output batches created with full lineage back to supplier lot, nothing to reconcile later.'],
              ['15:00', 'QC dashboard shows batches ready for release. One tap, released.'],
            ],
          },
          {
            role: 'The office',
            color: C.navyMid,
            hours: [
              ['08:30', 'Overnight EDI POs from three retailers already parsed into sales orders on the pipeline.'],
              ['11:00', 'Prints tomorrow’s dispatch pack, retailer-specific notes, pallet labels, COAs, in one go.'],
              ['14:20', 'Customer asks “which batch did we send you in Aug?”. Copy-paste dispatch number, screen shot, sent.'],
              ['17:00', 'Sync to Sage runs. Invoices are ready before the accountant asks.'],
            ],
          },
        ].map((p) => (
          <div key={p.role} style={{ background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${p.color}`, padding: '5mm 4mm' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', fontWeight: 700, color: C.navy, marginBottom: '3mm', letterSpacing: '-0.01em' }}>{p.role}</div>
            {p.hours.map(([t, b], i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '2mm 0', borderTop: i > 0 ? `1px dashed ${C.border}` : 'none' }}>
                <span style={{ fontSize: '8pt', fontWeight: 700, color: p.color, fontFamily: 'monospace', width: 32, flexShrink: 0, paddingTop: 2 }}>{t}</span>
                <span style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5 }}>{b}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <Pull>
        The measure of good operational software isn’t how impressive its dashboards are. It’s how many hours it hands back to the founder every week.
      </Pull>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 11: Integrations ----------

const IntegrationsPage = () => (
  <Page pageNum={11} bg={C.paper}>
    <PageHeader pageNum={11} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>06 · Integrations</SectionLabel>
      <H2>Nothing is impossible.<br/><Em>The honest question is where each integration sits today.</Em></H2>

      <div style={{ marginTop: '4mm', fontSize: '10pt', color: C.slate, lineHeight: 1.6, maxWidth: '175mm' }}>
        Three columns. Live today means an out-of-the-box customer can turn it on. Configuration-ready means the shape exists and a light adapter finishes the job during onboarding. Bespoke means yes, we will build it, quoted separately, against your requirement.
      </div>

      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4mm' }}>
        {[
          {
            title: 'Live today',
            sub: 'Out of the box',
            color: C.magenta,
            items: [
              ['REST API (v1)', 'Versioned, documented, key-authenticated. Read + write for orders, batches, dispatches, traces.'],
              ['CSV exports', 'Customers, suppliers, products, batches, dispatches, orders, traces. Scheduled or on-demand.'],
              ['AccountsIQ', 'Live customer, invoice and dispatch sync in production.'],
              ['Read-only DB replica', 'Optional. For Power BI, Metabase, or a customer\u2019s in-house analyst.'],
            ],
          },
          {
            title: 'Configuration-ready',
            sub: 'Adapter finished during onboarding',
            color: C.gold,
            items: [
              ['Xero, Sage, QuickBooks', 'Connector shape is in place. Customer-specific chart of accounts + tax codes mapped during onboarding.'],
              ['Retailer file-drop / SFTP', 'For non-EDI retailers who accept a scheduled PDF + CSV pack.'],
              ['Label printer SDKs', 'Zebra, Toshiba, and any TSPL / ZPL-compatible printer.'],
              ['Legacy Excel import', 'Bulk load customers, products, suppliers, opening stock from your current workbook.'],
            ],
          },
          {
            title: 'Bespoke build',
            sub: 'Quoted separately, no surprises',
            color: C.navyMid,
            items: [
              ['SAP Business One / B1 HANA', 'Fully feasible; scoped against your specific SAP configuration.'],
              ['Microsoft Dynamics 365 Business Central', 'Same. Feasible today, timeline depends on your BC extensions.'],
              ['Native retailer EDI', 'Tesco Connect, Musgrave, Aldi, SPAR, direct EDIFACT if your retailer requires it.'],
              ['Anything else', 'Bring the API doc. If it has one, we can talk to it.'],
            ],
          },
        ].map((col) => (
          <div key={col.title} style={{ background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${col.color}` }}>
            <div style={{ padding: '4.5mm 5mm 3mm', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: col.color }}>{col.title}</div>
              <div style={{ fontSize: '9pt', color: C.muted, marginTop: 2 }}>{col.sub}</div>
            </div>
            <div>
              {col.items.map(([t, b], i) => (
                <div key={i} style={{ padding: '3.5mm 5mm', borderBottom: i < col.items.length - 1 ? `1px dashed ${C.border}` : 'none' }}>
                  <div style={{ fontSize: '9.5pt', fontWeight: 700, color: C.navy, marginBottom: 2 }}>{t}</div>
                  <div style={{ fontSize: '8.5pt', color: C.slate, lineHeight: 1.5 }}>{b}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Two commitments below the matrix */}
      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
        <div style={{ padding: '5mm 6mm', background: C.navy, color: '#fff' }}>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 3 }}>
            Build once, share forever
          </div>
          <div style={{ fontSize: '9.5pt', color: 'rgba(255,255,255,0.88)', lineHeight: 1.55 }}>
            When one customer pays for a bespoke integration, we productise it. Every future customer benefits from the work at no extra cost. This keeps the platform coherent instead of a graveyard of one-off scripts.
          </div>
        </div>
        <div style={{ padding: '5mm 6mm', background: C.cream, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: 3 }}>
            Data out, always
          </div>
          <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.55 }}>
            CSV export of every core table on request. Full read-only DB access for the life of the contract if you want it. Full DB dump on exit within 48 hours. No exit fee, ever, in writing.
          </div>
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 12: Compliance & security ----------

const CompliancePage = () => (
  <Page pageNum={12}>
    <PageHeader pageNum={12} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>06 · Compliance & security</SectionLabel>
      <H2>Honest about what’s live,<br/><Em>honest about what’s in progress.</Em></H2>
      <Body>
        Food software vendors love to list every acronym in the compliance dictionary. We list only what we can prove today, and mark the rest as in progress. Because when a retailer’s auditor asks, that’s the answer that matters.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
        <div>
          <div style={{ fontSize: '9pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: C.green, marginBottom: '3mm' }}>&check; Live today</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {[
              ['MFA on every login', 'TOTP + WebAuthn / passkeys enforced for admin accounts.'],
              ['GDPR posture', 'DPO named. PII encrypted at rest with dedicated tenant keys. DSAR + erasure endpoints wired.'],
              ['AES-256 at rest, TLS 1.3 in transit', 'AWS KMS-managed keys. Per-tenant database isolation, no shared schema.'],
              ['Tamper-evident audit log', 'Every mutation cryptographically chained. Signed exports for auditors.'],
              ['EU-only hosting by default', 'AWS eu-west-1 (Dublin). Or deploy inside your own AWS account with our IaC.'],
              ['Daily encrypted backups', 'Point-in-time restore, 30 days retained. Restore drills every quarter.'],
            ].map(([t, b]) => (
              <li key={t} style={{ padding: '3mm 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '10pt', fontWeight: 700, color: C.navy }}>{t}</div>
                <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5, marginTop: 2 }}>{b}</div>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div style={{ fontSize: '9pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: C.amber, marginBottom: '3mm' }}>· In progress</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {[
              ['ISO/IEC 27001:2022 alignment', 'Controls mapped, evidence gathered, formal certification on the 2026 roadmap.'],
              ['Cyber Essentials (IASME scope)', 'Scoped, documentation drafted, assessment booked.'],
              ['Penetration test cadence', 'Annual third-party; results shared under NDA on request.'],
              ['Detailed security & architecture pack', 'Full technical brief available during evaluation on request.'],
            ].map(([t, b]) => (
              <li key={t} style={{ padding: '3mm 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '10pt', fontWeight: 700, color: C.navy }}>{t}</div>
                <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5, marginTop: 2 }}>{b}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div style={{ marginTop: '5mm', padding: '4mm 5mm', background: C.navy, color: '#fff', borderLeft: `3px solid ${C.gold}` }}>
        <div style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 4 }}>Why this matters</div>
        <div style={{ fontSize: '10pt', lineHeight: 1.55 }}>
          Retailer audits ask two questions: “can you trace it?” and “can you prove no one changed the trace?”. Keystone answers both by design, not by policy.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 12: Cashel Blue proof ----------

const ProofPage = () => (
  <Page pageNum={13} bg={C.cream}>
    <PageHeader pageNum={13} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>07 · Founding partner</SectionLabel>
      <H2>Cashel Blue.<br/><Em>Where every design decision was tested.</Em></H2>
      <Body>
        Ireland’s original farmhouse blue cheese, three generations of the Grubb family, exporting to nine markets, forty-plus internal SKUs, private-label lines with Tesco Finest and M&S Made Without. If Keystone couldn’t run their week, it couldn’t run anyone’s.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4mm' }}>
        {[
          ['40+', 'internal SKUs, retail + foodservice + gift'],
          ['9', 'export markets: UK, FR, BNL, DK, DE, US, CA, ME, AU'],
          ['12', 'private-label variants across the retailer mix'],
          ['400+', 'customer-mapped SKU codes maintained daily'],
        ].map(([k, v], i) => (
          <div key={i} style={{ padding: '4mm', background: C.paper, border: `1px solid ${C.border}`, textAlign: 'center' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '30pt', fontWeight: 700, color: C.magenta, lineHeight: 1 }}>{k}</div>
            <div style={{ fontSize: '8.5pt', color: C.slate, marginTop: 4, lineHeight: 1.3 }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '8mm', padding: '6mm 7mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.magenta}` }}>
        <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: 6 }}>
          Reference call available
        </div>
        <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.55 }}>
          Cashel Blue&rsquo;s QA &amp; Operations team will speak directly to prospective customers about how Keystone runs day-to-day on their floor. Long-form written testimonials are being collected for publication; in the meantime we&rsquo;d rather introduce you to the people using the software than paste words in their mouth. Ask us for an intro on the discovery call.
        </div>
      </div>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4mm' }}>
        {[
          ['Recall drill', 'From <em>“which flour lot?”</em> to <em>“which retailer, which dispatch, which pallet?”</em>, under a minute. On a Sunday. On the phone.'],
          ['Retailer audit', 'BRCGS auditor asked for the trace on a random batch. Cashel Blue opened Keystone, exported the signed PDF pack. Auditor closed the section.'],
          ['Friday afternoon', 'Order Pipeline replaced three spreadsheets, one WhatsApp thread, and a paper diary. The office got Friday afternoons back.'],
        ].map(([t, b], i) => (
          <div key={i} style={{ padding: '4mm', background: C.paper, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: 4 }}>{t}</div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: b }} />
          </div>
        ))}
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 13: What onboarding actually looks like ----------

const OnboardingPage = () => (
  <Page pageNum={14}>
    <PageHeader pageNum={14} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>08 · What onboarding actually looks like</SectionLabel>
      <H2>Live in weeks, not quarters.<br/><Em>Because software vendors that need six months are building you, not configuring software.</Em></H2>

      <div style={{ marginTop: '6mm' }}>
        {[
          { w: 'Week 0', title: 'Discovery on your floor', body: 'Two days on-site. We map your suppliers, your customers, your dispatch templates, your retailer codes. We watch a make day. We watch a Friday. We watch a dispatch. Then we write a fixed-fee scope in plain English, no billable hours, no “discovery phase” that never ends.' },
          { w: 'Week 1', title: 'Data lift', body: 'Your customers, products, retailer SKU mappings, dispatch templates, pallet-label formats, all migrated from spreadsheets into a live tenant. You review, we correct, we lock. Sandbox is up by Friday.' },
          { w: 'Week 2\u2013 3', title: 'Templates & training', body: 'Branded dispatch note, pallet label, COA templates configured for every customer that needs their own. One day of on-site training with the whole operational team. Not a webinar, sat next to them at their screens.' },
          { w: 'Week 4', title: 'Go-live & three-day hand-holding', body: 'First three days of live production, we sit on the phone with your office. First live dispatch pack goes out under our supervision. First recall drill runs by day five.' },
          { w: 'Month 2\u2013 3', title: 'Post-go-live hand-holding', body: 'Direct line to the build team, you talk to whoever wrote the code, not a support tier. Any tweak needed in the first thirty days is bundled into implementation. No side invoices.' },
        ].map((s, i) => (
          <div key={s.w} style={{ display: 'grid', gridTemplateColumns: '38mm 1fr', gap: '5mm', padding: '4mm 0', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
            <div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18pt', color: C.magenta, fontWeight: 700, lineHeight: 1, marginBottom: 4 }}>{s.w}</div>
              <div style={{ fontSize: '11pt', color: C.navy, fontWeight: 700, lineHeight: 1.3 }}>{s.title}</div>
            </div>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: s.body }} />
          </div>
        ))}
      </div>

      <div style={{ marginTop: '5mm', padding: '4mm 5mm', background: C.bgSoft, borderLeft: `3px solid ${C.gold}` }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: C.amber, marginBottom: 4 }}>The honest bit</div>
        <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.55 }}>
          Complex retailer EDI (Tesco / Aldi / Lidl / M&S), multi-site consolidation, or twelve months of historical data all extend the timeline. We’ll tell you before you sign, not three weeks in.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 14: Investment (no numbers, but shape of pricing) ----------

const InvestmentPage = () => (
  <Page pageNum={15} bg={C.cream}>
    <PageHeader pageNum={15} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>09 · What it costs</SectionLabel>
      <H2>Two lines on the invoice.<br/><Em>Recurring for “run”. One-off for “fit”.</Em></H2>
      <Body>
        We deliberately separate what it costs to <em>run</em> Keystone (a monthly licence) from what it costs to <em>fit</em> Keystone to your operation (a one-off implementation project). Nobody gets locked into a two-year contract because the vendor is amortising setup cost in the recurring fee.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
        {[
          { title: 'The licence', sub: 'Recurring, monthly', price: '€790 / site / month', body: 'Standard tier. Every core module unlocked. Unlimited reasonable-use users, no per-seat games. Annual billing available at a straight discount. Cancel any time, data exported as CSV within 48 hours.' },
          { title: 'Implementation', sub: 'One-off, fixed-fee', price: 'From €4,950', body: 'Bounded scope agreed in writing before we start. Discovery, data lift, templates, training, and thirty days of hand-holding, all in the number. Complex retailer EDI, historical data imports and accounts-package integrations quoted separately so you only pay for what you actually use.' },
        ].map((c) => (
          <div key={c.title} style={{ padding: '5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta }}>{c.sub}</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', color: C.navy, fontWeight: 700, marginTop: 4, marginBottom: 2, letterSpacing: '-0.01em' }}>{c.title}</div>
            <div style={{ fontSize: '13pt', color: C.magenta, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.01em' }}>{c.price}</div>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.55 }}>{c.body}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '6mm', padding: '5mm 6mm', background: C.navy, color: '#fff' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '9pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 4 }}>Better than a free trial</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', color: '#fff', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
              The 30-day pilot programme.
            </div>
          </div>
          <div style={{ fontSize: '10pt', color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
            A real, scaled-down implementation with your data on our screens for thirty days from €2,500. If you continue, it credits to full implementation. If you walk away within fourteen days of the pilot ending, <strong style={{ color: C.gold }}>half is refundable</strong>, and your data exports as CSVs, no penalty.
          </div>
        </div>
      </div>

      <div style={{ marginTop: '5mm', fontSize: '9pt', color: C.muted, fontStyle: 'italic', textAlign: 'center', lineHeight: 1.5 }}>
        Full published pricing lives at <span style={{ color: C.magenta }}>keystone.cloudvoro.com/site/pricing</span>. Every number is bounded, in writing, and quoted before you commit.
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 15: Not for us / for us ----------

const FitPage = () => (
  <Page pageNum={16}>
    <PageHeader pageNum={16} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>10 · Fit</SectionLabel>
      <H2>Who Keystone is for,<br/><Em>and who it isn’t.</Em></H2>
      <Body>
        We’d rather lose a sale than force-fit a customer. Below is what we look for. If you don’t recognise your business here, we’ll happily point you elsewhere.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
        <div style={{ padding: '5mm', background: C.bgSoft, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.green}` }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: C.green, marginBottom: '3mm' }}>&check; Built for you if…</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {[
              'You’re a batch-based food manufacturer, dairy, bakery, meat, prepared food, cheese, chocolate, deli, sauces.',
              'You supply retailers, foodservice or wholesale, with proper traceability requirements.',
              'You’re between 5 and 50 people, typically €1M to €20M turnover.',
              'You’re family-run or founder-run, and someone in the building still touches product every week.',
              'You want the audit log, dispatch quality and recall drills of a mid-market operator without the mid-market ERP price tag.',
              'You’d rather talk to the developer than a sales tier.',
            ].map((b, i) => (
              <li key={i} style={{ padding: '2mm 0', display: 'flex', gap: 8, fontSize: '9.5pt', color: C.slate, lineHeight: 1.5 }}>
                <span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>&check;</span>
                <span dangerouslySetInnerHTML={{ __html: b }} />
              </li>
            ))}
          </ul>
        </div>
        <div style={{ padding: '5mm', background: C.bgSoft, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.red}` }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: C.red, marginBottom: '3mm' }}>× Better served elsewhere if…</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {[
              'You’re a plant of 500+ people needing a full MES and PLC integration on the make line.',
              'You’re a €100M+ business with a shared-services IT team looking at SAP or Business Central.',
              'You need heavyweight demand planning, S&OP or multi-echelon inventory optimisation.',
              'You don’t make anything, you distribute other people’s finished product.',
              'Your operation is fine on paper and you have no compliance or growth pressure to change.',
            ].map((b, i) => (
              <li key={i} style={{ padding: '2mm 0', display: 'flex', gap: 8, fontSize: '9.5pt', color: C.slate, lineHeight: 1.5 }}>
                <span style={{ color: C.red, fontWeight: 700, flexShrink: 0 }}>×</span>
                <span dangerouslySetInnerHTML={{ __html: b }} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div style={{ marginTop: '6mm', padding: '4mm 5mm', background: C.paper, border: `1px dashed ${C.border}` }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: C.muted, marginBottom: 4 }}>The honest test</div>
        <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.6 }}>
          Book a twenty-minute call. We ask five questions about your operation. At the end of it, we’ll tell you honestly whether Keystone is the right fit, or which of our competitors we’d recommend instead. No pitch, no chase sequence.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ---------- Page 16: Closing / how to start ----------

const ClosingPage = () => (
  <Page pageNum={17} bg={C.navy}>
    <PageHeader pageNum={17} dark />
    <div style={{ paddingTop: '16mm', color: '#fff', height: 'calc(100% - 22mm)', display: 'flex', flexDirection: 'column' }}>
      <SectionLabel color={C.gold}>11 · How to start</SectionLabel>
      <H2 style={{ color: '#fff', fontSize: '32pt', marginBottom: '4mm' }}>
        Twenty minutes<br/><Em color={C.gold}>and a piece of paper.</Em>
      </H2>
      <p style={{ fontSize: '11pt', lineHeight: 1.5, color: 'rgba(255,255,255,0.82)', marginBottom: '6mm', maxWidth: '140mm' }}>
        We don’t do free trials of production software, they’re theatre. Here’s how a real conversation with us goes:
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4mm', marginBottom: '6mm' }}>
        {[
          { n: '1', t: 'A twenty-minute call', b: 'On video. We ask about your operation, your retailers, your worst Friday. You ask us anything. No slides, no salesperson.' },
          { n: '2', t: 'A one-page scope', b: 'Within 48 hours. Fixed fee for implementation, plain-English list of what’s in and what isn’t. Yours to keep, whether we work together or not.' },
          { n: '3', t: 'Then you decide', b: 'A 30-day pilot with your real data, or straight to a full implementation. Either way, we start with the discovery visit on your floor.' },
        ].map((s) => (
          <div key={s.n} style={{ padding: '4mm', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderTop: `2px solid ${C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '28pt', color: C.gold, fontWeight: 700, lineHeight: 1, marginBottom: '2mm' }}>{s.n}</div>
            <div style={{ fontSize: '10.5pt', fontWeight: 700, color: '#fff', marginBottom: 4 }}>{s.t}</div>
            <div style={{ fontSize: '8.5pt', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: s.b }} />
          </div>
        ))}
      </div>

      <div style={{ padding: '5mm 6mm', background: 'linear-gradient(135deg, rgba(233,30,99,0.18) 0%, rgba(201,169,97,0.12) 100%)', border: `1px solid ${C.gold}55` }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: C.gold, marginBottom: '3mm' }}>Get in touch</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5mm' }}>
          {[
            ['Direct line', '+353 85 181 0310', 'You’ll talk to the person who wrote the code.'],
            ['Email', 'hello@cloudvoro.com', 'Reply within a working day, usually within the hour.'],
            ['Book online', 'keystone.cloudvoro.com/site/contact', 'Pick a 20-minute slot. Video call. No SDR funnel.'],
          ].map(([l, v, sub]) => (
            <div key={l}>
              <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{l}</div>
              <div style={{ fontSize: '10.5pt', color: '#fff', fontWeight: 700, marginBottom: 2, wordBreak: 'break-word' }}>{v}</div>
              <div style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.6)', lineHeight: 1.45 }} dangerouslySetInnerHTML={{ __html: sub }} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ padding: '4mm 0', textAlign: 'left' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontSize: '13.5pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.45, maxWidth: '160mm' }}>
          We believe the people who make Europe’s food shouldn’t need an enterprise ERP to prove it’s safe.
          <span style={{ color: C.gold }}> If you believe that too, we should talk.</span>
        </div>
      </div>

      <div style={{ paddingTop: '4mm', borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '8pt', color: 'rgba(255,255,255,0.5)' }}>
        <div>Cloudvoro Ltd &middot; 1 Emmett St, Thurles, Co. Tipperary, E41 YH05, Ireland &middot; CRO 795787 &middot; VAT IE4490902SH</div>
        <div style={{ color: C.gold }}>keystone.cloudvoro.com</div>
      </div>
    </div>
  </Page>
);

// ---------- Assemble ----------

function BrochureBuyerPack() {
  const [downloading, setDownloading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showGate, setShowGate] = React.useState(false);
  const [lead, setLead] = React.useState({ email: '', name: '', company: '', website: '' });
  const [gateBusy, setGateBusy] = React.useState(false);
  const [gateError, setGateError] = React.useState('');

  const doDownload = async () => {
    setError('');
    setDownloading(true);
    try {
      await downloadServerPDF('/brochure/buyer-pack', 'Keystone-Buyer-Pack.pdf');
    } catch (e) {
      setError(e.message || 'PDF render failed');
    } finally {
      setDownloading(false);
    }
  };

  const onDownload = () => {
    if (sessionStorage.getItem('kx.bro.lead')) {
      doDownload();
    } else {
      setGateError('');
      setShowGate(true);
    }
  };

  const submitGate = async (e) => {
    e.preventDefault();
    if (!lead.email.trim() || gateBusy) return;
    setGateBusy(true);
    setGateError('');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/brochure-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: lead.email.trim(), name: lead.name.trim(), company: lead.company.trim(),
          website: lead.website, source_path: '/brochure/sales',
        }),
      });
      const d = await r.json();
      if (d && d.ok) {
        sessionStorage.setItem('kx.bro.lead', '1');
        setShowGate(false);
        doDownload();
      } else {
        setGateError('That didn\u2019t go through. Check the email and try again.');
      }
    } catch {
      setGateError('That didn\u2019t go through. Check the email and try again.');
    } finally {
      setGateBusy(false);
    }
  };

  return (
    <div className="sales-brochure-stack" style={{ background: '#EDEEF0', minHeight: '100vh', padding: '32px 0', fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Screen-only controls */}
      <div className="brochure-controls" data-testid="sales-brochure-controls" style={{
        position: 'sticky',
        top: 12,
        zIndex: 20,
        maxWidth: '210mm',
        margin: '0 auto 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 6,
        boxShadow: '0 4px 20px rgba(10, 37, 64, 0.10)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <KeystoneMark size={22} variant="light" />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, letterSpacing: '0.04em' }}>Keystone Buyer Pack</div>
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{TOTAL_PAGES} pages · A4 · server-rendered PDF</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-testid="sales-brochure-print"
            onClick={() => window.print()}
            style={{
              padding: '9px 16px', background: '#fff', color: C.navy,
              border: `1px solid ${C.border}`, borderRadius: 4,
              fontSize: 12, fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            Print
          </button>
          <button
            data-testid="sales-brochure-download"
            onClick={onDownload}
            disabled={downloading}
            style={{
              padding: '9px 18px', background: C.navy, color: '#fff',
              border: 'none', borderRadius: 4,
              fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', cursor: downloading ? 'wait' : 'pointer',
              opacity: downloading ? 0.7 : 1,
            }}
          >
            {downloading ? 'Rendering…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ maxWidth: '210mm', margin: '0 auto 16px', padding: '10px 14px', background: '#FEE2E2', color: C.red, border: `1px solid ${C.red}44`, borderRadius: 4, fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Email gate before PDF download */}
      {showGate && (
        <div data-testid="brochure-gate-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(10,37,64,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <form onSubmit={submitGate} data-testid="brochure-gate-form" style={{
            background: '#fff', borderRadius: 8, padding: '26px 26px 22px', width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(6,26,46,0.35)',
          }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 21, fontWeight: 700, color: C.navy, letterSpacing: '-0.01em' }}>
              Where should we say the brochure went?
            </div>
            <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.55, marginTop: 8 }}>
              Leave a work email and the PDF downloads immediately. No drip sequence: at most one human follow-up, from the founder.
            </div>
            <input type="text" value={lead.website} onChange={(e) => setLead({ ...lead, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" name="website"
              style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />
            <input
              type="email" required autoFocus placeholder="you@yourcompany.ie" value={lead.email}
              onChange={(e) => setLead({ ...lead, email: e.target.value })}
              data-testid="brochure-gate-email"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 14, padding: '12px 13px', fontSize: 14, border: `1px solid ${C.border}`, borderRadius: 4 }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                type="text" placeholder="Name (optional)" value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                data-testid="brochure-gate-name"
                style={{ flex: 1, minWidth: 0, padding: '12px 13px', fontSize: 14, border: `1px solid ${C.border}`, borderRadius: 4 }}
              />
              <input
                type="text" placeholder="Company (optional)" value={lead.company}
                onChange={(e) => setLead({ ...lead, company: e.target.value })}
                data-testid="brochure-gate-company"
                style={{ flex: 1, minWidth: 0, padding: '12px 13px', fontSize: 14, border: `1px solid ${C.border}`, borderRadius: 4 }}
              />
            </div>
            {gateError && <div data-testid="brochure-gate-error" style={{ fontSize: 12.5, color: C.red, marginTop: 8 }}>{gateError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button type="button" data-testid="brochure-gate-cancel" onClick={() => setShowGate(false)} style={{
                padding: '10px 16px', background: '#fff', color: C.slate, border: `1px solid ${C.border}`, borderRadius: 4,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button type="submit" data-testid="brochure-gate-submit" disabled={gateBusy} style={{
                padding: '10px 20px', background: C.magenta, color: '#fff', border: 'none', borderRadius: 4,
                fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                cursor: gateBusy ? 'wait' : 'pointer', opacity: gateBusy ? 0.7 : 1,
              }}>
                {gateBusy ? 'One sec\u2026' : 'Email me nothing, just download'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>
              GDPR-friendly: stored as a sales lead only, never shared, deleted on request.
            </div>
          </form>
        </div>
      )}

      {/* Pages */}
      <CoverPage />
      <BeliefPage />
      <RealityPage />
      <WhyGenericPage />
      <SpinePage />
      <WorkflowPage
        pageNum={6}
        stageLabel="Stage 01 · Inwards"
        stageColor={C.magenta}
        title="Every drum, sheet and litre"
        italic="tagged the moment it crosses your dock."
        body="No goods enter the factory without a supplier lot, a date and an accept/reject decision. When the flour supplier issues a recall six weeks later, the lot number tells you which finished batches used it, before you even lift the phone."
        screens={InwardScreen}
        bullets={[
          'Delivery record ties supplier → supplier lot → internal item, per line, per drum.',
          'Antibiotic screen, SCC, TBC, fat & protein captured on milk intake, goes straight onto the COA.',
          'Silo movements auto-created, no separate warehousing module to maintain.',
          'Every downstream trace query starts here. Nothing hides upstream.',
        ]}
      />
      <WorkflowPage
        pageNum={7}
        stageLabel="Stage 02 · Production"
        stageColor={C.magentaDark}
        title="One make day"
        italic="that becomes the whole audit trail."
        body="The make sheet the supervisor already fills in, but tied at line-level to the milk intake, the ingredient lots, the packaging lots and the batches it produces. Recipe is captured as-built, not as-planned."
        screens={MakeDayScreen}
        bullets={[
          'Milk, ingredients, packaging, output batches, four sections, one sheet, one save.',
          'Supplier-lot auto-suggest so the supervisor never re-types the same lot twice.',
          'Output batches inherit the full upstream chain automatically, no manual linking.',
          'Batch corrections don’t rewrite history; they add a signed amendment.',
        ]}
      />
      <WorkflowPage
        pageNum={8}
        stageLabel="Stage 03 · Outwards"
        stageColor={C.navyMid}
        title="A dispatch note that speaks"
        italic="the retailer’s language, not yours."
        body="Every retailer wants their code, their template, their pallet-label format, their COA structure. Keystone stores it all against your customer record and reprints it in their language every time, down to the catch-weight totals on the invoice."
        screens={DispatchScreen}
        bullets={[
          'Customer → SKU mapping means the dispatch note prints in the retailer’s code system automatically.',
          'Catch-weight rounding: nominal kg gets scaled up/down to <em>actual weighed</em> kg, per case, per pallet.',
          'Retailer-specific templates: dispatch note, pallet label, COA all follow the customer’s spec.',
          'EDI DESADV / ASN queued and sent automatically when the retailer is on integration.',
        ]}
      />
      <WorkflowPage
        pageNum={9}
        stageLabel="Stage 04 · Traceability"
        stageColor={C.red}
        title="A mock recall"
        italic="that finishes before the kettle boils."
        body="Start from any point, a supplier lot, a milk intake, a make day, a finished batch, a dispatch, a customer PO, and Keystone builds the full report in seconds. Affected batches, downstream customers, draft comms, printable PDF response pack."
        screens={RecallScreen}
        bullets={[
          'One-click from any starting point. Ingredient lot, milk intake, make day, batch, dispatch, customer.',
          'Affected customers with dispatch counts and unit totals, ready to hand to your quality lead.',
          'Customer draft communications generated per affected dispatch. Copy, print, export, never sent automatically.',
          'Signed PDF response pack: cover, summary, batches, dispatches, notes, status history, drafts. Ready for the auditor.',
        ]}
      />
      <DayInLifePage />
      <IntegrationsPage />
      <CompliancePage />
      <ProofPage />
      <OnboardingPage />
      <InvestmentPage />
      <FitPage />
      <ClosingPage />

      <style>{`
        @page { size: A4; margin: 0; }
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

        /* Screen: scale the fixed A4 pages down to fit narrow viewports so
           the brochure is actually readable on phones. Uses CSS zoom which,
           unlike transform scale, shrinks the layout box too. Print
           rendering (Chromium page.pdf) uses the print block below
           and is unaffected. */
        @media screen and (max-width: 820px) {
          html, body { overflow-x: hidden; }
          .sales-brochure-stack { padding: 12px 0 !important; }
          .brochure-page {
            zoom: calc((100vw - 16px) / 210mm);
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .brochure-controls {
            max-width: calc(100vw - 20px) !important;
            padding: 8px 12px !important;
          }
        }

        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .brochure-controls { display: none !important; }
          .sales-brochure-stack { padding: 0 !important; }
          .brochure-page {
            box-shadow: none !important;
            margin: 0 !important;
            max-height: 297mm !important;
            overflow: hidden !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: always;
            break-after: page;
            transform: none !important;
            zoom: 1 !important;
          }
          .brochure-page:last-of-type {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
      `}</style>
    </div>
  );
}

export default BrochureBuyerPack;
